// src/services/userWallService.ts
import apiClient from '@/api/apiClient';
import { Post, UserProfile } from '@/stores/UserWallStore';

interface PostResponse {
    data: Post[];
    meta: {
        pagination: {
            page: number;
            pageSize: number;
            pageCount: number;
            total: number;
        };
    };
}

interface CreatePostData {
    content: string;
    postType: Post['postType'];
    privacy: Post['privacy'];
    media?: File[];
    workId?: string;
}

interface CommentData {
    id: string;
    content: string;
    userId: string;
    postId: string;
    createdAt: string;
    user: {
        id: string;
        username: string;
        firstName: string;
        lastName: string;
        avatar?: string;
    };
}

class UserWallService {
    // Get user profile
    async getUserProfile(userId: string): Promise<UserProfile> {
        try {
            const response = await apiClient.get(`/api/users/${userId}/profile`);
            return response.data;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            throw error;
        }
    }

    // Get user posts
    async getUserPosts(userId: string, page = 1, pageSize = 20): Promise<PostResponse> {
        try {
            const response = await apiClient.get(`/api/users/${userId}/posts`, {
                params: {
                    'pagination[page]': page,
                    'pagination[pageSize]': pageSize,
                    'sort[0]': 'createdAt:desc',
                    'populate': '*'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching user posts:', error);
            throw error;
        }
    }

    // Create new post
    async createPost(postData: CreatePostData): Promise<Post> {
        try {
            const formData = new FormData();
            formData.append('data', JSON.stringify({
                content: postData.content,
                postType: postData.postType,
                privacy: postData.privacy,
                work: postData.workId
            }));

            if (postData.media) {
                postData.media.forEach((file, index) => {
                    formData.append(`files.media`, file);
                });
            }

            const response = await apiClient.post('/api/user-posts', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            return response.data.data;
        } catch (error) {
            console.error('Error creating post:', error);
            throw error;
        }
    }

    // Like post
    async likePost(postId: string): Promise<void> {
        try {
            await apiClient.post(`/api/posts/${postId}/like`);
        } catch (error) {
            console.error('Error liking post:', error);
            throw error;
        }
    }

    // Unlike post
    async unlikePost(postId: string): Promise<void> {
        try {
            await apiClient.delete(`/api/posts/${postId}/like`);
        } catch (error) {
            console.error('Error unliking post:', error);
            throw error;
        }
    }

    // Add comment
    async addComment(postId: string, content: string): Promise<CommentData> {
        try {
            const response = await apiClient.post(`/api/posts/${postId}/comments`, {
                data: {
                    content,
                    post: postId
                }
            });
            return response.data.data;
        } catch (error) {
            console.error('Error adding comment:', error);
            throw error;
        }
    }

    // Get comments
    async getComments(postId: string, page = 1, pageSize = 10): Promise<{ data: CommentData[], meta: any }> {
        try {
            const response = await apiClient.get(`/api/posts/${postId}/comments`, {
                params: {
                    'pagination[page]': page,
                    'pagination[pageSize]': pageSize,
                    'sort[0]': 'createdAt:desc',
                    'populate': 'user'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching comments:', error);
            throw error;
        }
    }

    // Follow user
    async followUser(userId: string): Promise<void> {
        try {
            await apiClient.post(`/api/users/${userId}/follow`);
        } catch (error) {
            console.error('Error following user:', error);
            throw error;
        }
    }

    // Unfollow user
    async unfollowUser(userId: string): Promise<void> {
        try {
            await apiClient.delete(`/api/users/${userId}/follow`);
        } catch (error) {
            console.error('Error unfollowing user:', error);
            throw error;
        }
    }

    // Share post
    async sharePost(postId: string): Promise<void> {
        try {
            await apiClient.post(`/api/posts/${postId}/share`);
        } catch (error) {
            console.error('Error sharing post:', error);
            throw error;
        }
    }

    // Save post
    async savePost(postId: string): Promise<void> {
        try {
            await apiClient.post(`/api/posts/${postId}/save`);
        } catch (error) {
            console.error('Error saving post:', error);
            throw error;
        }
    }

    // Unsave post
    async unsavePost(postId: string): Promise<void> {
        try {
            await apiClient.delete(`/api/posts/${postId}/save`);
        } catch (error) {
            console.error('Error unsaving post:', error);
            throw error;
        }
    }

    // Update profile
    async updateProfile(userId: string, profileData: Partial<UserProfile>): Promise<UserProfile> {
        try {
            const response = await apiClient.put(`/api/users/${userId}/profile`, {
                data: profileData
            });
            return response.data.data;
        } catch (error) {
            console.error('Error updating profile:', error);
            throw error;
        }
    }

    // Upload cover photo
    async uploadCoverPhoto(userId: string, file: File): Promise<string> {
        try {
            const formData = new FormData();
            formData.append('files', file);

            const response = await apiClient.post(`/api/users/${userId}/cover-photo`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            return response.data.url;
        } catch (error) {
            console.error('Error uploading cover photo:', error);
            throw error;
        }
    }

    // Get user statistics
    async getUserStats(userId: string): Promise<any> {
        try {
            const response = await apiClient.get(`/api/users/${userId}/stats`);
            return response.data;
        } catch (error) {
            console.error('Error fetching user stats:', error);
            throw error;
        }
    }

    // Get user's works
    async getUserWorks(userId: string, page = 1, pageSize = 12): Promise<any> {
        try {
            const response = await apiClient.get(`/api/works`, {
                params: {
                    'filters[owner][id][$eq]': userId,
                    'pagination[page]': page,
                    'pagination[pageSize]': pageSize,
                    'sort[0]': 'createdAt:desc',
                    'populate': '*'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching user works:', error);
            throw error;
        }
    }

    // Get user's licenses
    async getUserLicenses(userId: string, page = 1, pageSize = 12): Promise<any> {
        try {
            const response = await apiClient.get(`/api/licenses`, {
                params: {
                    'filters[licensee][id][$eq]': userId,
                    'pagination[page]': page,
                    'pagination[pageSize]': pageSize,
                    'sort[0]': 'createdAt:desc',
                    'populate': '*'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching user licenses:', error);
            throw error;
        }
    }
}

export default new UserWallService();